'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

export type DashboardView = 'overview' | 'agent-wise' | 'parameters' | 'policy-violations' | 'kpi'

interface DashboardNavProps {
  activeView: DashboardView
  onViewChange: (view: DashboardView) => void
}

interface NavItem {
  id: DashboardView
  label: string
  icon: React.ElementType
}

const navItems: NavItem[] = [
  { id: 'overview', label: 'Dashboard', icon: Icons.dashboard },
  { id: 'agent-wise', label: 'Agent Wise', icon: Icons.users },
  { id: 'parameters', label: 'Parameters', icon: Icons.settings },
  { id: 'policy-violations', label: 'AI Policy Checklist', icon: Icons.clipboardList },
  { id: 'kpi', label: 'KPI', icon: Icons.trendingUp },
]

export function DashboardNav({ activeView, onViewChange }: DashboardNavProps) {
  return (
    <nav className='relative'>
      <div className='flex items-center gap-2 overflow-x-auto pb-1'>
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = activeView === item.id

          return (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              className={cn(
                'relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200',
                'whitespace-nowrap',
                isActive
                  ? 'text-white'
                  : 'text-brand-muted hover:bg-white/5 hover:text-brand-cornflower'
              )}
            >
              {/* Active background with gradient */}
              {isActive && (
                <motion.div
                  layoutId='activeTab'
                  className='absolute inset-0 rounded-full bg-gradient-to-r from-brand-navy to-brand-purple shadow-lg'
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30,
                  }}
                />
              )}

              {/* Content */}
              <span className='relative z-10 flex items-center gap-2'>
                <Icon className='h-4 w-4' strokeWidth={2} />
                {item.label}
              </span>

              {/* Shine effect on hover */}
              {!isActive && (
                <motion.div
                  className='absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/10 to-transparent'
                  initial={{ x: '-100%', opacity: 0 }}
                  whileHover={{ x: '100%', opacity: 1 }}
                  transition={{ duration: 0.6 }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Subtle bottom border */}
      <div className='mt-4 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent' />
    </nav>
  )
}

